import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckVerifiedDomainStatus } from "@evennt-tech/api-v2-external";
import { RequestKey } from "api/request-key";
import { apiV2 } from "api/firebase";
import { extractOrigin } from "tools/url/extractDomain";

type Args = {
  enabled?: boolean;
};

export type CheckResult = {
  result: Map<string, CheckVerifiedDomainStatus>;
  isReady: boolean;
};

const normalizeCheckDomain = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const origin = extractOrigin(trimmed) ?? extractOrigin(`https://${trimmed}`);
  return (origin ?? trimmed).toLowerCase();
};

export const useCheckVerifiedDomains = (
  domains: string[],
  { enabled = true }: Args = {}
): CheckResult => {
  const sortedUnique = useMemo(() => {
    const set = new Set<string>();
    domains.forEach((d) => {
      const domain = normalizeCheckDomain(d);
      if (domain) set.add(domain);
    });
    return Array.from(set).sort();
  }, [domains]);

  const queryEnabled = enabled && sortedUnique.length > 0;

  const { data, isFetched } = useQuery({
    queryKey: [RequestKey.CheckVerifiedDomains, sortedUnique.join(",")],
    refetchOnWindowFocus: false,
    enabled: queryEnabled,
    queryFn: () =>
      apiV2.verifiedDomain.check({
        domains: sortedUnique,
      }),
  });

  return useMemo(() => {
    const result = new Map<string, CheckVerifiedDomainStatus>();
    (data ?? []).forEach((item) => {
      result.set(item.domain.toLowerCase(), item.status);
    });
    return {
      result,
      isReady: !queryEnabled || isFetched,
    };
  }, [data, queryEnabled, isFetched]);
};
